import { ENTITY_PREFIX, TypedEntityId } from "../../../shared/models/entity.model";

/**
 * A tenant's content-catalog (Atlas) entity id: a show (`SH:`) or a channel (`LC:`), the only two
 * things a tenant maps to — never an interactive (`IP:`/`IF:`). The backend stores it as a bare
 * string and never checks the prefix, so this type and the form's validator are the only things
 * keeping an id that can't be a tenant out of the field.
 */
export type EntityId = TypedEntityId<typeof ENTITY_PREFIX.SHOW | typeof ENTITY_PREFIX.CHANNEL>;

/** The prefixes `EntityId` covers; pass to `hasEntityPrefix` to narrow a free-text value to one. */
export const TENANT_ENTITY_PREFIXES = [ENTITY_PREFIX.SHOW, ENTITY_PREFIX.CHANNEL] as const;

/** Max length of a tenant's description; mirrors the backend `@Size(max = 500)`. */
export const TENANT_DESCRIPTION_MAX_LENGTH = 500;

/** Max number of search keywords (`searchAliases`); mirrors the backend `@Size(max = 5)`. */
export const MAX_SEARCH_KEYWORDS = 5;

export interface TenantResponse {
    id: string; // plain UUID
    themeId: string; // plain UUID
    // Null for tenants that aren't represented in the catalog (e.g. sweepstakes) — the backend
    // allows it to be unset.
    entityId: EntityId | null;
    name: string;
    description: string | null;
    prioritizeOverCallIn: boolean;
    themeName: string;
    tenantLogoUrl: string | null;
    searchAliases: string[];
    createdAt: string; // ISO 8601
    updatedAt: string; // ISO 8601
}

export interface TenantsResponse {
    items: TenantResponse[];
    nextCursor: string | null;
    size: number;
    hasNext: boolean;
}

/**
 * Fields sent when creating a tenant; mirrors the backend tenant create DTO. Everything but
 * `themeId` and `name` is optional so partial payloads work — the rest is validated by the BE.
 */
export interface CreateTenantRequest {
    id?: string; // plain UUID
    themeId: string; // plain UUID
    // `null` (not omitted) when the tenant has no catalog entity. The BE rejects a present-but-
    // blank value ("entityId must not be blank") but treats `null` as a deliberate unset.
    entityId?: EntityId | null;
    name: string;
    description?: string;
    prioritizeOverCallIn?: boolean;
    searchAliases?: string[];
}

/**
 * Fields sent when updating a tenant; same shape as create minus the client-supplied id.
 * Sending `entityId: null` clears an entity id a tenant already has.
 */
export interface UpdateTenantRequest {
    themeId: string; // plain UUID
    entityId?: EntityId | null;
    name: string;
    description?: string;
    prioritizeOverCallIn?: boolean;
    searchAliases?: string[];
}

/**
 * What the tenant form emits on submit — the create/update fields it owns, already trimmed.
 * `entityId` is `null` when the field is left blank, which clears an existing entity id on update
 * and simply leaves it unset on create.
 */
export interface TenantFormValue {
    themeId: string;
    entityId?: EntityId | null;
    name: string;
    description: string;
    prioritizeOverCallIn: boolean;
    searchAliases: string[];
}
